import React, { useMemo } from 'react';
import classnames from 'classnames';
import { getFlagURL, prettyMoney } from '@scramble/utils';
import { useTranslation } from 'react-i18next';
import { useGetInvestedCountries } from '@/query/queries/investing/useGetInvestedCountries';
import { useRoundStatus } from '@/hooks/shared/useRoundStatus';
import s from './index.module.scss';

export const InvestedCountries = () => {
  const { t } = useTranslation('investing');
  const { isActive } = useRoundStatus();
  const { data } = useGetInvestedCountries();
  const otherCountries = useMemo(() => data?.countries?.slice(4), [data]);

  if (!isActive || !data) return null;

  return (
    <div className={s.wrapper}>
      <div className={s.title}>
        {t('investing:countries.investments_by_country')}
      </div>
      <div className={s.slider}>
        <Country country={data.countries[0]} />
        <Country country={data.countries[1]} />
        <Country country={data.countries[2]} />
        <Country country={data.countries[3]} />
        <OtherCountries countries={otherCountries} />
      </div>
    </div>
  );
};

const Country = ({ country }) => {
  if (!country) return null;

  return (
    <div className={s.place}>
      <div className={classnames(s.place_flag)}>
        <img
          src={getFlagURL(country.country_code)}
          alt={country.country_code}
          width={40}
          height={40}
        />
      </div>
      <div className={s.place_content}>
        <div className={s.place_amount}>
          {prettyMoney(country.allocated_amount)}
        </div>
        <div className={s.place_name}>{country.country_name}</div>
      </div>
    </div>
  );
};

const OtherCountries = ({ countries }) => {
  const { t } = useTranslation();
  const totalAmount = React.useMemo(
    () =>
      countries.reduce((acc, country) => {
        acc += country.allocated_amount;
        return acc;
      }, 0),
    [countries],
  );

  if (!totalAmount) return null;

  return (
    <div className={s.other}>
      <div className={s.other_flags}>
        {countries.map((country, index) => {
          if (countries.length === 3 || index <= 1) {
            return (
              <img
                key={country.country_code}
                src={getFlagURL(country.country_code)}
                alt={country.country_code}
                width={40}
                height={40}
              />
            );
          }
        })}
        {countries.length > 3 && (
          <span className={s.other_more}>+{countries.length - 2}</span>
        )}
      </div>
      <div className={s.other_info}>
        <div className={s.other_value}>{prettyMoney(totalAmount)}</div>
        <div className={s.other_name}>
          {t('investing:countries.other_countries')}
        </div>
      </div>
    </div>
  );
};
